import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";

const RAZ_LOGO = "/manus-storage/raz_logo_negativo_200222c8.png";

export default function AuthMagic() {
  const [, navigate] = useLocation();
  const { login: authLogin } = useAuth();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");

  const verifyMutation = trpc.clientAuth.verifyMagicLink.useMutation();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) {
      setStatus("error");
      setErrorMsg("Token não encontrado. Solicite um novo link de acesso.");
      return;
    }

    verifyMutation.mutate(
      { token },
      {
        onSuccess: (data) => {
          // authLogin grava no localStorage E atualiza o estado do AuthContext —
          // pra navegação seguinte já enxergar o usuário logado.
          authLogin({
            token:      data.token,
            userId:     data.userId,
            name:       data.name ?? "",
            email:      data.email,
            role:       (data.role ?? "viewer") as "admin" | "editor" | "viewer",
            clientId:   data.clientId ?? null,
            isRazAdmin: data.isRazAdmin ?? false,
            mustChangePassword: !!(data as any).mustChangePassword,
          });
          localStorage.removeItem("RAZ_DEV_CLIENT_ID");
          setStatus("success");
          // Se o magic-link cair num usuário com senha temporária, vai pro reset
          const redirectTo = (data as any).mustChangePassword ? "/redefinir-senha" : "/";
          setTimeout(() => navigate(redirectTo), 1200);
        },
        onError: (err) => {
          setStatus("error");
          setErrorMsg(err.message ?? "Link inválido ou expirado.");
        },
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-[var(--solar-bg)]">
      <div className="relative w-full max-w-sm text-center">
        <div className="mx-auto mb-10 w-fit rounded-xl bg-[var(--solar-ink)] shadow-[var(--elev-2)] px-5 py-3 flex items-center justify-center">
          <img src={RAZ_LOGO} alt="RAZ Consulting" className="h-7 w-auto" onError={e => { (e.target as HTMLImageElement).style.display = "none"; }} />
        </div>

        <div className="rounded-xl border border-[var(--solar-line)] bg-white shadow-[var(--elev-2)] p-10">
          {status === "loading" && (
            <>
              <div className="w-14 h-14 rounded-full border-2 border-[var(--solar-line)] border-t-[var(--brand)] animate-spin mx-auto mb-6" />
              <h2 className="text-[var(--solar-ink)] text-lg font-semibold mb-2">Verificando link...</h2>
              <p className="text-[var(--solar-mute)] text-sm">Aguarde um momento.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-14 h-14 rounded-full bg-[var(--brand-soft)] flex items-center justify-center mx-auto mb-6">
                <svg className="w-7 h-7 text-[var(--brand)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-[var(--solar-ink)] text-lg font-semibold mb-2">Acesso confirmado!</h2>
              <p className="text-[var(--ink-2)] text-sm">Redirecionando para o painel...</p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-6">
                <svg className="w-7 h-7 text-[var(--destructive)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 className="text-[var(--solar-ink)] text-lg font-semibold mb-2">Link inválido</h2>
              <p className="text-[var(--ink-2)] text-sm mb-6">{errorMsg}</p>
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-[var(--solar-ink)] text-white font-semibold py-3 rounded-[10px] text-sm shadow-[var(--elev-1)] hover:opacity-90 transition"
              >
                Solicitar novo link →
              </button>
            </>
          )}
        </div>

        <p className="text-[var(--solar-mute)] text-xs mt-6">
          Problemas?{" "}
          <a href="mailto:marketing@razconsulting.com.br" className="text-[var(--solar-ink)] hover:underline transition">
            marketing@razconsulting.com.br
          </a>
        </p>
      </div>
    </div>
  );
}
